import { formatNameFirstLastInitial } from '../utils/userState';
import { generateStepSummaryHTML, type StepSummaryContext } from './stepSummaryHtml';

interface User {
  email?: string;
  name?: string;
}

interface StepProgress {
  step_number: number;
  step_title: string;
  completed: boolean;
  data?: any;
}

interface InventoryItem {
  who?: string;
  why?: string;
  feeling?: string;
  impacts?: string[];
  myPart?: string;
  fear?: string;
  whoHarmed?: string;
  how?: string;
  defects?: string[];
  alternative?: string;
}

// Step descriptions for SAA 12 Steps
const STEP_DESCRIPTIONS: Record<number, string> = {
  1: "Made a decision to turn our will and our lives over to the care of God as we understood God.",
  2: "Came to believe that a Power greater than ourselves could restore us to sanity.",
  3: "Made a decision to turn our will and our lives over to the care of God as we understood God.",
  4: "Made a searching and fearless moral inventory of ourselves.",
  5: "Admitted to God, to ourselves, and to another human being the exact nature of our wrongs.",
  6: "Were entirely ready to have God remove all these defects of character.",
  7: "Humbly asked God to remove our shortcomings.",
  8: "Made a list of all persons we had harmed, and became willing to make amends to them all.",
  9: "Made direct amends to such people wherever possible, except when to do so would injure them or others.",
  10: "Continued to take personal inventory and when we were wrong promptly admitted it.",
  11: "Sought through prayer and meditation to improve our conscious contact with God as we understood God, praying only for knowledge of God's will for us and the power to carry that out.",
  12: "Having had a spiritual awakening as the result of these steps, we tried to carry this message to sexaholics who still suffer, and to practice these principles in all our affairs."
};

// Action descriptions for each step
const ACTION_DESCRIPTIONS: Record<number, Record<number, string>> = {
  1: {
    1: "Understand what Step One in SAA is about and why it's important",
    2: "Learn how to work Step One in SAA",
    3: "Write out my Step One",
    4: "Review my Step One with my Sponsor",
    5: "Read my First Step in a meeting",
    6: "Take Step One: Admit that I am powerless over compulsive sexual addiction - that my life has become unmanageable"
  },
  2: {
    1: "Read the SAA Green Book about Step Two",
    2: "Read guidance on working Step Two",
    3: "Answer Step Two questions",
    4: "Take Step Two: I am willing to believe that there is a power greater than myself that can restore me to sanity"
  },
  3: {
    1: "Read the SAA Green Book about Step Three",
    2: "Read guidance on working Step Three",
    3: "Answer Step Three questions",
    4: "Read the Third Step Prayer — and write my own if I choose",
    5: "Share and review your work with your sponsor",
    6: "Take Step Three"
  },
  4: {
    1: "Understand what Step Four in SAA is about and why it's important",
    2: "Learn how to work Step Four in SAA",
    3: "Complete your Step Four inventories",
    4: "Take Step Four: Schedule a time to share your Step Four inventories with your sponsor"
  },
  5: {
    1: "Read the SAA Green Book about Step Five",
    2: "How to work Step Five (guidance & video)",
    3: "Present Step Four inventories to my sponsor",
    4: "Identify my character defects",
    5: "Share and review my character defects with my sponsor",
    6: "Take Step Five"
  },
  6: {
    1: "Learn about Step Six from the Green Book",
    2: "Complete Step Six questions",
    3: "Take Step Six: Become ready to have God remove defects"
  },
  7: {
    1: "Read Green Book: Step Seven",
    2: "How to work Step Seven (guidance & video)",
    3: "Read the Seventh Step Prayer — and write my own if I choose",
    4: "Share and review your work with your sponsor",
    5: "Take Step Seven: read the Seventh Step Prayer aloud"
  },
  8: {
    1: "Learn about Step Eight from the Green Book",
    2: "Complete Step Eight questions",
    3: "Take Step Eight: Make list of persons harmed and become willing to make amends"
  },
  9: {
    1: "Learn about Step Nine from the Green Book",
    2: "Complete Step Nine questions",
    3: "Take Step Nine: Make direct amends to persons harmed"
  },
  10: {
    1: "Learn about Step Ten from the Green Book",
    2: "Complete Step Ten questions",
    3: "Take Step Ten: Continue personal inventory and promptly admit wrongs"
  },
  11: {
    1: "Read Green Book: Step Eleven",
    2: "Read guidance on working Step Eleven",
    3: "Define my daily quiet time plan and practice it",
    4: "Share and review your work with your sponsor",
    5: "Take Step Eleven"
  },
  12: {
    1: "Read Green Book: Step Twelve",
    2: "Read guidance on working Step Twelve",
    3: "Define my service plan",
    4: "Share and review your work with your sponsor",
    5: "Take Step Twelve"
  }
};

export class HTMLService {
  private static formatDate(): string {
    const date = new Date();
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Convert newlines to <br> tags to preserve line breaks in HTML
  private static preserveNewlines(text: string): string {
    if (!text) return '';
    return text.replace(/\n/g, '<br>');
  }

  static generate12StepStatusHTML(user: User, stepProgress: StepProgress[]): string {
    const currentDate = this.formatDate();
    
    let html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>recoverycommons.com</title>
    <script>
        // Auto-trigger print if print parameter is present
        window.addEventListener('load', function() {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('print') === 'true') {
                setTimeout(function() {
                    window.print();
                }, 500);
            }
        });
    </script>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            background-color: white;
            color: black;
            font-size: 16px;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid black;
            padding-bottom: 20px;
        }
        .title {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 10px;
            color: black;
        }
        .user-info {
            font-size: 18px;
            margin-bottom: 5px;
            color: black;
        }
        .date {
            font-size: 18px;
            margin-bottom: 5px;
            color: black;
        }
        .generated-by {
            font-size: 14px;
            font-style: italic;
            color: black;
        }
        .step {
            margin-bottom: 30px;
            page-break-inside: avoid;
        }
        .step-header {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 10px;
            color: black;
        }
        .step-description {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
            color: black;
        }
        .step-status {
            font-size: 18px;
            margin-bottom: 20px;
            color: black;
        }
        .status-label {
            font-weight: normal;
        }
        .status-pill {
            display: inline-block;
            padding: 6px 16px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 14px;
            margin-left: 10px;
        }
        .status-completed {
            background-color: #28a745;
            color: white;
        }
        .status-in-progress {
            background-color: #ffc107;
            color: black;
        }
        .status-todo {
            background-color: #6c757d;
            color: black;
        }
        .action {
            margin-left: 20px;
            margin-bottom: 12px;
            font-size: 16px;
            display: flex;
            align-items: flex-start;
        }
        .action-number {
            width: 80px;
            flex-shrink: 0;
            margin-right: 15px;
            color: black;
        }
        .action-status {
            width: 60px;
            flex-shrink: 0;
            margin-right: 15px;
            font-size: 20px;
            color: #28a745;
            font-weight: bold;
        }
        .action-text {
            flex: 1;
            color: black;
        }
        .action-todo {
            color: #999;
            font-size: 14px;
            font-style: italic;
        }
        .question {
            margin-left: 60px;
            margin-bottom: 20px;
            font-size: 16px;
        }
        .question-text {
            font-weight: bold;
            text-decoration: underline;
            margin-bottom: 8px;
            color: black;
        }
        .answer {
            margin-left: 30px;
            font-family: 'Courier New', monospace;
            margin-bottom: 10px;
            color: black;
            font-size: 16px;
        }
        .answer-filled {
            font-weight: bold;
        }
        .answer-blank {
            color: #999;
            font-weight: normal;
        }
        .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 14px;
            color: #666;
        }
        @page {
            margin-top: 0.5in;
            margin-bottom: 0.5in;
        }
        @media print {
            body { margin: 0; }
            .step { page-break-inside: avoid; }
            .footer { margin-top: 30px; }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="title">SAA 12-Step Status</div>
        <div class="user-info">${user.email || user.name || 'Unknown'}</div>
        <div class="date">${currentDate}</div>
        <div class="generated-by">Recovery Commons (recoverycommons.com)</div>
    </div>
`;

    // Step progress - optimized with pre-built strings
    const stepHtml = stepProgress.map((step) => {
      // Determine step status - ONLY completed actions make it "in progress"
      let stepStatusClass = 'status-todo';
      let stepStatusText = 'To Do';
      
      if (step.completed) {
        stepStatusClass = 'status-completed';
        stepStatusText = 'Completed';
      } else if (step.data) {
        // Check for completed actions - this is the ONLY way a step can be "in progress"
        if (step.data.actions && step.data.actions.length > 0) {
          const completedActions = step.data.actions.filter((action: any) => action.completed).length;
          const totalActions = step.data.actions.length;
          if (completedActions === totalActions) {
            stepStatusClass = 'status-completed';
            stepStatusText = 'Completed';
          } else if (completedActions > 0) {
            stepStatusClass = 'status-in-progress';
            stepStatusText = 'In Progress';
          }
          // If no completed actions, it stays "To Do" regardless of other data
        } else if (step.data.questions && step.data.questions.length > 0) {
          const answeredQuestions = step.data.questions.filter((question: any) => question.answer && question.answer.trim()).length;
          const totalQuestions = step.data.questions.length;
          if (answeredQuestions === totalQuestions) {
            stepStatusClass = 'status-completed';
            stepStatusText = 'Completed';
          } else if (answeredQuestions > 0) {
            stepStatusClass = 'status-in-progress';
            stepStatusText = 'In Progress';
          }
          // If no answered questions, it stays "To Do"
        }
      }

      let stepContent = `
    <div class="step">
        <div class="step-header">Step ${step.step_number}:</div>
        <div class="step-description">${STEP_DESCRIPTIONS[step.step_number] || ''}</div>
        <div class="step-status">
            <span class="status-label">Step Status:</span>
            <span class="status-pill ${stepStatusClass}">${stepStatusText}</span>
        </div>`;

      // Add step-specific details if there's work
      if (step.data) {
        if (step.data.actions) {
          const actionsHtml = step.data.actions.map((action: any, actionIndex: number) => {
            const actionNumber = actionIndex + 1;
            const actionDescription = ACTION_DESCRIPTIONS[step.step_number]?.[actionNumber] || `Action ${actionNumber}`;
            const isCompleted = action.completed;
            
            let actionContent = `
        <div class="action">
            <div class="action-number">Action ${actionNumber}</div>
            <div class="action-status">${isCompleted ? '✓' : '<span class="action-todo">To Do</span>'}</div>
            <div class="action-text">${actionDescription}</div>
        </div>`;

            // Handle questions for specific steps (Step 2 and Step 3, Action 3)
            if (actionNumber === 3) {
              if (step.step_number === 2 && step.data.step2_questions) {
                const step2Questions = [
                  'Describe what spirituality / religion looked like for you growing up, its impact on you, and any feelings you have about it.',
                  'Describe the role, if any, that spirituality / religion has in your life currently.',
                  'Describe your higher power if you have one (e.g., perfectionist judge, kind grandpa, vending machine, not there)',
                  'Describe any feelings of anger you had or have about a higher power.',
                  'Recount any times you have prayed for something or made a deal with your higher power in moments of distress, and what happened after.',
                  'If appropriate, describe how someone in your life abused their authority over you and how that impacted your view of a higher power.'
                ];
                
                const questionsHtml = step2Questions.map((questionText, qIndex) => {
                  const questionId = `q${qIndex + 1}`;
                  const questionData = step.data.step2_questions[questionId];
                  const answer = questionData?.answer || '';
                  const answerClass = answer.trim() ? 'answer answer-filled' : 'answer answer-blank';
                  const displayAnswer = answer.trim() ? this.preserveNewlines(answer.trim()) : '(blank)';
                  
                  return `
        <div class="question">
            <div class="question-text">${questionText}</div>
            <div class="${answerClass}">${displayAnswer}</div>
        </div>`;
                }).join('');
                
                actionContent += questionsHtml;
              } else if (step.step_number === 3 && step.data.step3_questions) {
                const step3Questions = [
                  'Who or what made my decisions for me while I was compulsively sexually acting out?',
                  'What fears do I have about making a decision to turn my will and life over to a higher power of my understanding?',
                  'Which problems have I tried to resolve through willpower?',
                  'How has self-will and my reactions to life events made me and others suffer? Be specific.',
                  'Who or what makes my decisions for me in recovery?',
                  'Is my current concept of a higher power still working, and how might I need to change my concept of a higher power?',
                  'What are specific examples of how I turned my life and will over to my addiction?',
                  'How would my day look if I were to turn my will and my life over to the care of a higher power of my understanding?',
                  'Which thoughts and behaviors am I willing to turn over right now, and which am I not yet?'
                ];
                
                const questionsHtml = step3Questions.map((questionText, qIndex) => {
                  const questionId = `q${qIndex + 1}`;
                  const questionData = step.data.step3_questions[questionId];
                  const answer = questionData?.answer || '';
                  const answerClass = answer.trim() ? 'answer answer-filled' : 'answer answer-blank';
                  const displayAnswer = answer.trim() ? this.preserveNewlines(answer.trim()) : '(blank)';
                  
                  return `
        <div class="question">
            <div class="question-text">${questionText}</div>
            <div class="${answerClass}">${displayAnswer}</div>
        </div>`;
                }).join('');
                
                actionContent += questionsHtml;
              }
            }
            
            return actionContent;
          }).join('');
          
          stepContent += actionsHtml;
        } else if (step.data.questions) {
          const questionsHtml = step.data.questions.map((question: any, qIndex: number) => {
            if (question.answer && question.answer.trim()) {
              return `
        <div class="question">
            <div class="question-text">Q${qIndex + 1}: ${question.answer}</div>
        </div>`;
            }
            return '';
          }).join('');
          
          stepContent += questionsHtml;
        }
        
        // Add custom prayer for Step 3 if it exists (check at step level after actions/questions)
        if (step.step_number === 3 && step.data.prayer_text !== undefined) {
          const prayerAnswer = step.data.prayer_text || '';
          const prayerClass = prayerAnswer.trim() ? 'answer answer-filled' : 'answer answer-blank';
          const displayPrayer = prayerAnswer.trim() ? this.preserveNewlines(prayerAnswer.trim()) : '(blank)';
          
          stepContent += `
        <div class="question">
            <div class="question-text">My Third Step Prayer:</div>
            <div class="${prayerClass}">${displayPrayer}</div>
        </div>`;
        }
      }

      stepContent += `
    </div>`;
      
      return stepContent;
    }).join('');

    html += stepHtml + `
    <div class="footer">
        Recovery Commons (recoverycommons.com)
    </div>
</body>
</html>`;

    return html;
  }

  static generateStepFourInventoriesHTML(
    user: User, 
    resentments: InventoryItem[], 
    fears: InventoryItem[], 
    harms: InventoryItem[]
  ): string {
    const currentDate = this.formatDate();
    
    let html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>recoverycommons.com</title>
    <script>
        // Auto-trigger print if print parameter is present
        window.addEventListener('load', function() {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('print') === 'true') {
                setTimeout(function() {
                    window.print();
                }, 500);
            }
        });
    </script>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            margin: 0;
            padding: 20px;
            background-color: white;
            color: black;
            font-size: 16px;
        }
        .header {
            text-align: center;
            margin-bottom: 25px;
            border-bottom: 2px solid black;
            padding-bottom: 15px;
        }
        .title {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 8px;
            color: black;
        }
        .user-info {
            font-size: 18px;
            margin-bottom: 4px;
            color: black;
        }
        .date {
            font-size: 18px;
            margin-bottom: 4px;
            color: black;
        }
        .generated-by {
            font-size: 14px;
            font-style: italic;
            color: black;
        }
        .inventory-section {
            margin-bottom: 30px;
        }
        .inventory-title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 12px;
            color: black;
        }
        .entry {
            margin-bottom: 15px;
            page-break-inside: avoid;
        }
        .entry-header {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 8px;
            color: black;
        }
        .entry-details {
            margin-left: 20px;
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 5px;
            background-color: white;
        }
        .field {
            margin-bottom: 6px;
        }
        .field-label {
            font-weight: bold;
            font-size: 14px;
            color: black;
        }
        .field-value {
            font-size: 16px;
            margin-left: 10px;
            font-family: 'Courier New', monospace;
            color: black;
        }
        .divider {
            border-top: 1px solid #ddd;
            margin: 15px 0;
        }
        .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 14px;
            color: #666;
        }
        @page {
            margin-top: 0.5in;
            margin-bottom: 0.5in;
        }
        @media print {
            body { margin: 0; }
            .entry { page-break-inside: avoid; }
            .footer { margin-top: 30px; }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="title">Step Four Inventories</div>
        <div class="user-info">${user.email || user.name || 'Unknown'}</div>
        <div class="date">${currentDate}</div>
        <div class="generated-by">Recovery Commons (recoverycommons.com)</div>
    </div>
`;

    // Resentments Inventory
    if (resentments.length > 0) {
      html += `
    <div class="inventory-section">
        <div class="inventory-title">Resentments Inventory (${resentments.length} entries)</div>
`;

      resentments.forEach((item, index) => {
        html += `
        <div class="entry">
            <div class="entry-header">${item.who || 'Unknown'}</div>
            <div class="entry-details">
                <div class="field">
                    <span class="field-label">Why:</span>
                    <span class="field-value">${this.preserveNewlines(item.why || '')}</span>
                </div>
                <div class="field">
                    <span class="field-label">Feelings:</span>
                    <span class="field-value">${this.preserveNewlines(item.feeling || '')}</span>
                </div>
                <div class="field">
                    <span class="field-label">Impacted:</span>
                    <span class="field-value">${item.impacts?.join(', ') || ''}</span>
                </div>
                <div class="field">
                    <span class="field-label">My Part:</span>
                    <span class="field-value">${this.preserveNewlines(item.myPart || '')}</span>
                </div>
            </div>
        </div>
`;
      });

      html += `
    </div>
`;
    }

    // Fears Inventory
    if (fears.length > 0) {
      html += `
    <div class="inventory-section">
        <div class="inventory-title">Fears Inventory (${fears.length} entries)</div>
`;

      fears.forEach((item, index) => {
        html += `
        <div class="entry">
            <div class="entry-header">${item.fear || 'Unknown'}</div>
            <div class="entry-details">
                <div class="field">
                    <span class="field-label">Why:</span>
                    <span class="field-value">${this.preserveNewlines(item.why || '')}</span>
                </div>
                <div class="field">
                    <span class="field-label">Impacted:</span>
                    <span class="field-value">${item.impacts?.join(', ') || ''}</span>
                </div>
            </div>
        </div>
`;
      });

      html += `
    </div>
`;
    }

    // Harms Inventory
    if (harms.length > 0) {
      html += `
    <div class="inventory-section">
        <div class="inventory-title">Harms Done Inventory (${harms.length} entries)</div>
`;

      harms.forEach((item, index) => {
        html += `
        <div class="entry">
            <div class="entry-header">${item.whoHarmed || 'Unknown'}</div>
            <div class="entry-details">
                <div class="field">
                    <span class="field-label">How:</span>
                    <span class="field-value">${this.preserveNewlines(item.how || '')}</span>
                </div>
                <div class="field">
                    <span class="field-label">Defects:</span>
                    <span class="field-value">${item.defects?.join(', ') || ''}</span>
                </div>
                <div class="field">
                    <span class="field-label">What could I have done differently:</span>
                    <span class="field-value">${this.preserveNewlines(item.alternative || '')}</span>
                </div>
            </div>
        </div>
`;
      });

      html += `
    </div>
`;
    }

    html += `
    <div class="footer">
        Recovery Commons (recoverycommons.com)
    </div>
</body>
</html>`;

    return html;
  }

  static generateGettingStartedSummaryHTML(
    user: User,
    actions: { id: number; completed: boolean }[],
    threeCircles: { inner: string[]; middle: string[]; outer: string[] },
    sponsorStatus?: string
  ): string {
    const currentDate = this.formatDate();
    const memberName = user.name
      ? formatNameFirstLastInitial(user.name, 'Recovery Commons member')
      : 'Recovery Commons member';

    const SPONSOR_LABELS: Record<string, string> = {
      have_sponsor: 'I have a sponsor',
      looking: "I'm looking for a sponsor",
      ask_in_meeting: "I'll ask someone in a meeting",
    };

    const ACTION_LINES: Record<number, string> = {
      1: 'Read Green Book: Introduction & Our Addiction',
      2: 'Read Green Book: Our Fellowship',
      3: 'My sponsor',
      4: 'Read Green Book: Defining Abstinence & The Three Circles',
      5: 'Watch: The 3 Circles of SAA',
      6: 'Define my 3 Circles',
      7: 'Share and review your work with your sponsor',
      8: 'Ready for Step One',
    };

    const ACTION_ORDER = [1, 2, 3, 4, 5, 6, 7, 8];

    const hasCircles =
      threeCircles.inner.length > 0 ||
      threeCircles.middle.length > 0 ||
      threeCircles.outer.length > 0;

    const detailBelowNote = (actionId: number) => {
      if (actionId === 6 && hasCircles) {
        return ' <span class="detail-below">(see My 3 Circles below)</span>';
      }
      return '';
    };

    const formatActionLine = (id: number, completed: boolean) => {
      let line = `${id}. ${ACTION_LINES[id]}`;
      if (id === 3 && sponsorStatus && SPONSOR_LABELS[sponsorStatus]) {
        line += completed ? `: ${SPONSOR_LABELS[sponsorStatus]}` : '';
      }
      if (id === 5 && completed) {
        return `${line} — <a href="https://www.youtube.com/watch?v=VqWwh_rUZuY">https://www.youtube.com/watch?v=VqWwh_rUZuY</a>${detailBelowNote(id)}`;
      }
      return `${line}${detailBelowNote(id)}`;
    };

    const isCompleted = (id: number) => actions.some((a) => a.id === id && a.completed);

    const completedLines = ACTION_ORDER.filter((id) => isCompleted(id)).map(
      (id) => `<li>${formatActionLine(id, true)}</li>`
    );

    const remainingLines = ACTION_ORDER.filter((id) => !isCompleted(id)).map(
      (id) => `<li>${formatActionLine(id, false)}</li>`
    );

    const renderCircleList = (title: string, items: string[]) => {
      if (items.length === 0) return '';
      const lis = items.map((item) => `<li>${this.preserveNewlines(item.replace(/</g, '&lt;'))}</li>`).join('');
      return `
        <div class="circle-block">
          <h3>${title}</h3>
          <ul>${lis}</ul>
        </div>`;
    };

    const circlesHtml = hasCircles
      ? `
    <div class="section">
      <h2>My 3 Circles</h2>
      ${renderCircleList('Inner circle — behaviors I identify as acting out', threeCircles.inner)}
      ${renderCircleList('Middle circle — slippery slope / warning signs', threeCircles.middle)}
      ${renderCircleList('Outer circle — healthy / supports my recovery', threeCircles.outer)}
    </div>`
      : '';

    const actionsHtml =
      completedLines.length > 0
        ? `
    <div class="section">
      <h2>Completed work</h2>
      <ul class="completed-list">${completedLines.join('')}</ul>
    </div>`
        : '';

    const remainingHtml =
      remainingLines.length > 0
        ? `
    <div class="section">
      <h2>Remaining work</h2>
      <ul class="remaining-list">${remainingLines.join('')}</ul>
    </div>`
        : '';

    return this.wrapStepProgressDocument(
      'Getting Started — step work progress',
      memberName,
      currentDate,
      actionsHtml + remainingHtml + circlesHtml
    );
  }

  /** Per-step sponsor share summary (Steps 1–12). */
  static generateStepSummaryHTML(context: StepSummaryContext): string {
    return generateStepSummaryHTML(context);
  }

  private static wrapStepProgressDocument(
    title: string,
    memberName: string,
    currentDate: string,
    bodySections: string
  ): string {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title} — Recovery Commons</title>
    <script>
        window.addEventListener('load', function() {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('print') === 'true') {
                setTimeout(function() { window.print(); }, 500);
            }
        });
    </script>
    <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 24px; color: #111; line-height: 1.5; }
        h1 { font-size: 1.5rem; margin-bottom: 0.25rem; }
        .meta { color: #555; font-size: 0.95rem; margin-bottom: 1.5rem; }
        h2 { font-size: 1.15rem; margin: 1.25rem 0 0.5rem; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem; }
        h3 { font-size: 1rem; margin: 0.75rem 0 0.35rem; color: #333; }
        .section { margin-bottom: 1rem; }
        ul { margin: 0.25rem 0 0.75rem 1.25rem; }
        .completed-list li { margin-bottom: 0.35rem; }
        .remaining-list li { margin-bottom: 0.35rem; color: #555; }
        .detail-below { color: #555; font-size: 0.9rem; font-style: italic; }
        .circle-block { margin-bottom: 0.75rem; }
        .privacy { margin-top: 2rem; padding: 12px; background: #fff3cd; border-left: 4px solid #ffc107; font-size: 0.9rem; }
        .footer { text-align: center; margin-top: 2rem; font-size: 0.85rem; color: #666; }
        @media print { body { margin: 0.5in; } }
    </style>
</head>
<body>
    <h1>${title}</h1>
    <div class="meta">${memberName} · ${currentDate}</div>
    ${bodySections}
    <div class="privacy">
      <strong>Privacy notice:</strong> This summary link expires 7 days after it was created. After that date, the link will no longer work and the summary will be deleted.
    </div>
    <div class="footer">Recovery Commons (recoverycommons.com)</div>
</body>
</html>`;
  }
}